import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGraphql } from 'utils/hooks/useGraphql';

import { SELECT_TILESET_MODAL } from 'modules/workflows/constants';
import {
  closeModal,
  getNextNodeId,
  getStartNode,
  getWorkflowState,
  importTileset,
  selectNodes,
} from 'modules/workflows/ducks';
import { ErrorBanner } from 'ui/banner';
import { BUTTON_SIZES, Button } from 'ui/button';
import { InputSearch } from 'ui/inputs';
import { MODAL_SIZES, MODAL_VARIANTS, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';

import { EmptyTilesets } from './EmptyTilesets';
import { SelectedTileset } from './SelectedTileset';
import { CONTENT } from './constants';
import {
  RetryLink,
  StyledContent,
  TilesetOption,
  TilesetOptionContainer,
  TilesetSelection,
} from './styles';
import { getTilesetsQuery, incrementTilesetMutation } from './tilesets.graphql';
import { Tileset } from './types';
import { hydrateTileset } from './utils';

export type TilesetModalProps = {
  startX?: number;
  startY?: number;
  /** Whether the modal is open */
  isOpen: boolean;
};

export const INSERT_ACTION_ID = 'select-tileset';

export const SelectTilesetModal = ({ isOpen, startX, startY }: TilesetModalProps) => {
  const wfState = useSelector(getWorkflowState);
  const { data, isLoading, error, refetch } = useGraphql(getTilesetsQuery, {
    fetchPolicy: 'no-cache',
  });

  const allTilesets = data?.workflowFragments?.results || [];
  const [tilesets, setTilesets] = useState<[Tileset]>(allTilesets);
  const [tileset, setTileset] = useState<any>(null);
  const [searchString, setSearchString] = useState<string>('');

  const [incrementTileset] = useMutation(incrementTilesetMutation, {
    variables: {
      id: tileset?.id,
    },
  });

  useEffect(() => {
    if (isLoading) return;
    if (allTilesets.length > 0) {
      setTileset(allTilesets[0]);
    }
    setTilesets(allTilesets);
  }, [allTilesets]);

  const dispatch = useDispatch();
  const nextNodeId = useSelector(getNextNodeId);
  const startNode = useSelector(getStartNode);

  // handlers
  const handleCloseModal = () => dispatch(closeModal(SELECT_TILESET_MODAL));

  // Using start node X, Y as a default in case coordinates are null or undefined
  // when tileset tile is dragged onto the canvas
  const handleImport = () => {
    const hydratedTileset = hydrateTileset(
      {
        tilesetData: tileset?.data,
        nextNodeId,
        startX: startX ?? startNode?.x,
        startY: startY ?? startNode?.y,
      },
      wfState,
    );
    incrementTileset();
    dispatch(importTileset(hydratedTileset));
    dispatch(selectNodes(Object.keys(hydratedTileset.nodes).map((x) => x.toString())));
    handleCloseModal();
  };

  const handleSearch = (searchQuery: string) => {
    setSearchString(searchQuery);
    if (!searchQuery || searchQuery.length === 0) {
      setTilesets(allTilesets);
    } else {
      setTilesets(
        allTilesets.filter(
          (t: Tileset) => t.name.includes(searchQuery) || t.description.includes(searchQuery),
        ),
      );
    }
  };

  const handleDelete = () => {
    setTileset(null);
    refetch();
  };

  return (
    <Modal
      id={SELECT_TILESET_MODAL}
      isModalOpen={isOpen}
      onClose={handleCloseModal}
      size={MODAL_SIZES.LARGE}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader>{CONTENT.modalHeader}</ModalHeader>
      {allTilesets.length || isLoading ? (
        <ModalBody>
          <StyledContent>
            <TilesetSelection>
              <InputSearch
                id="tileset-searchbar"
                value={searchString}
                onChange={handleSearch}
                placeholder={CONTENT.searchPlaceholder}
              />
              <TilesetOptionContainer>
                {tilesets.map((t: Tileset) => (
                  <TilesetOption
                    key={t.id}
                    onClick={() => setTileset(tilesets.find((f: Tileset) => f.id === t.id))}
                    data-test={'tileset-option'}
                    isSelected={tileset?.id === t.id}
                  >
                    <p>{t.name}</p>
                  </TilesetOption>
                ))}
              </TilesetOptionContainer>

              {error && (
                <ErrorBanner>
                  <>
                    {CONTENT.errorMessage}
                    <br />
                    <RetryLink onClick={refetch}>{CONTENT.retryMessage}</RetryLink>
                  </>
                </ErrorBanner>
              )}
            </TilesetSelection>

            <SelectedTileset tileset={tileset} onDeleteTileset={handleDelete} />
          </StyledContent>
        </ModalBody>
      ) : (
        <EmptyTilesets />
      )}
      <ModalFooter>
        {tileset && (
          <Button
            autoFocus
            isDisabled={!tileset || isLoading}
            data-test={INSERT_ACTION_ID}
            onClick={handleImport}
            size={BUTTON_SIZES.LARGE}
          >
            {CONTENT.insertButton}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};
